import { Component, Input } from '@angular/core';
import { absDecimalString, decimalSign, formatDecimalAmount } from '../../../utils';

/**
 * Single place that decides how an advance balance reads, so the convention is
 * identical on the ledger, the labours list and (when it exists) the salary sheet.
 *
 * A negative balance is a correct state — the labour handed back more than they owed
 * and the company owes them. It is never shown as a negative number under a
 * "given"-style label; it gets its own "Refundable" wording and colour, and the
 * amount itself is always rendered as a positive magnitude next to that label.
 *
 * Values are decimal strings and are formatted as such — never through parseFloat.
 */
@Component({
  selector: 'app-advance-balance',
  standalone: false,
  templateUrl: './advance-balance.component.html',
})
export class AdvanceBalanceComponent {
  @Input() value: string | null | undefined = null;
  /** Hides the wording and keeps only the coloured amount — for dense table cells. */
  @Input() compact: boolean = false;

  protected get sign(): -1 | 0 | 1 {
    return decimalSign(this.value);
  }

  /** Magnitude only: the label carries the direction, so the minus sign would double up. */
  protected get amount(): string {
    return formatDecimalAmount(absDecimalString(this.value));
  }

  protected get label(): string {
    if (this.sign < 0) return 'Refundable — company owes';
    if (this.sign > 0) return 'Outstanding — labour owes';

    return 'Settled';
  }

  protected get shortLabel(): string {
    if (this.sign < 0) return 'Refundable';
    if (this.sign > 0) return 'Outstanding';

    return 'Settled';
  }

  protected get textClass(): string {
    if (this.sign < 0) return 'text-primary';
    if (this.sign > 0) return 'text-warning';

    return 'text-muted';
  }

  protected get badgeClass(): string {
    if (this.sign < 0) return 'badge-soft-primary';
    if (this.sign > 0) return 'badge-soft-warning';

    return 'badge-secondary';
  }
}
